// src/lib/rtc.js — PeerManager (mediasoup SFU) provider-ready + recover-safe
import { socket } from "./sessionApi";
import * as mediasoupClient from "mediasoup-client";

console.log("[RTC] build", "2025-12-24_audio_debug_v2");


class PeerManager {
  constructor({ getLocalStream, onPeerStream, onPeerRemove, iceServers }) {
    // External hooks
    this.getLocalStream = getLocalStream || (() => null);
    this.onPeerStream = onPeerStream || (() => {});
    this.onPeerRemove = onPeerRemove || (() => {});
    this.iceServers = iceServers || []; // (not used by mediasoup-client; kept for compat)

    // mediasoup
    this.device = null;
    this.sendT = null;
    this.recvT = null;

    // Local media (capture-owned by UI; we only reference it)
    this.localStream = null;
    this.producers = new Map(); // kind -> producer

    // Remote
    this.consumers = new Map(); // producerId -> { consumer, peerId, kind }
    this.peerStreams = new Map(); // peerId -> MediaStream
    this.prodToPeer = new Map(); // producerId -> peerId

    // ✅ NEW: track known remote producers to avoid “missed producer” races
    this._knownProducers = new Set();

    // Socket handler bookkeeping
    this._wired = false;
    this._socketHandlers = [];

    // Recovery / stability
    // Recovery / stability
    this._closed = false;
    this._recovering = false;

    // per-transport recover throttle (send/recv kunnen apart falen)
    this._lastRecoverAtSend = 0;
    this._lastRecoverAtRecv = 0;

    // fail tracking + escalation
    this._recoverFailStreak = 0;
    this._lastFullResetAt = 0;

    this._iceWatchdogTimerSend = null;
    this._iceWatchdogTimerRecv = null;
    this._iceWatchdogMs = 6000;

    // Race control for setLocalStream()
    this._localGen = 0;
  }

  /* -------------------------
   * Socket wiring
   * ------------------------- */
  wireSocketHandlers() {
    if (this._wired) return;
    this._wired = true;

    const onNewProducer = async ({ peerId, producerId }) => {
      if (this._closed) return;
      if (!producerId) return;
      if (peerId) this.prodToPeer.set(producerId, peerId);

      try {
        await this._consume(producerId);
      } catch {}
    };

    const onPeerLeft = ({ peerId }) => {
      if (this._closed) return;
      if (!peerId) return;
      this._removePeer(peerId);
      try {
        this.onPeerRemove?.(peerId);
      } catch {}
    };

    socket.on("rtc:new-producer", onNewProducer);
    socket.on("rtc:peer-left", onPeerLeft);

    this._socketHandlers.push(["rtc:new-producer", onNewProducer]);
    this._socketHandlers.push(["rtc:peer-left", onPeerLeft]);
  }

  _unwireSocketHandlers() {
    try {
      for (const [evt, fn] of this._socketHandlers) {
        socket.off(evt, fn);
      }
    } catch {}
    this._socketHandlers = [];
    this._wired = false;
  }

  /* -------------------------
   * Public API
   * ------------------------- */

  async _consumeMany(producerEntries, concurrency = 4) {
    const queue = [...producerEntries];
    const workers = Array.from({ length: concurrency }, async () => {
      while (queue.length && !this._closed) {
        const item = queue.shift();
        if (!item) return;
        const { producerId, peerId } = item;
        if (!producerId) continue;
        if (peerId) this.prodToPeer.set(producerId, peerId);
        try {
          await this._consume(producerId);
        } catch {}
      }
    });
    await Promise.all(workers);
  }

  // ✅ NEW: safety net to prevent “missed producer” races (read-only)
  async _syncProducersOnce(concurrency = 4) {
    if (this._closed) return;
    if (!this.recvT || !this.device) return;

    const res = await new Promise((res) =>
      socket.emit("rtc:listProducers", {}, (r) => res(r))
    );

    if (!res?.ok || !Array.isArray(res.producers)) return;

    const missing = res.producers.filter(
      ({ producerId }) =>
        producerId && !this._knownProducers.has(producerId)
    );

    if (!missing.length) return;

    console.warn("[RTC] syncing missing producers:", missing.length);

    try {
      await this._consumeMany(missing, concurrency);
    } catch {}
  }

  async joinRoom() {
    // 1) Join SFU room (idempotent on your server)
    const joinRes = await new Promise((res) =>
      socket.emit("rtc:join", {}, (r) => res(r))
    );
    if (!joinRes?.ok) throw new Error(joinRes?.error || "rtc:join failed");

    // 2) Load device once
    if (!this.device) {
      this.device = new mediasoupClient.Device();
      await this.device.load({ routerRtpCapabilities: joinRes.rtpCapabilities });
    }

    // 3) Create transports
    await this._createTransports();

    // 4) Produce local tracks (if UI already has them)
    const ls = this.getLocalStream?.();
    if (ls) await this.setLocalStream(ls);

    // 5) Consume already-existing producers
    const list = Array.isArray(joinRes.producers) ? joinRes.producers : [];
    await this._consumeMany(list, 4);

    // ✅ NEW: final safety sync (covers race between join snapshot & new-producer events)
    await this._syncProducersOnce(4);
  }

  /**
   * IMPORTANT: PeerManager never stops capture tracks.
   * If stream is null -> we just close producers (stop sending).
   */
  async setLocalStream(stream) {
    const gen = ++this._localGen;
    if (this._closed) return;

    // If null -> stop sending (close producers), but don't touch capture devices
    if (!stream) {
      this.localStream = null;
      this._closeLocalProducers();
      return;
    }

    this.localStream = stream;

    // If transport not ready yet, we just keep ref; joinRoom() will call again after transports exist
    if (!this.sendT) return;

    // Close existing producers first
    this._closeLocalProducers();

    // Pick LIVE tracks only
    const isLive = (t) => t && t.readyState === "live";
    const audio = stream.getAudioTracks().find(isLive) || null;
    const video = stream.getVideoTracks().find(isLive) || null;

// ✅ NEW: self-heal audio producer if missing
const hasAudioProducer = !!this.producers.get("audio");
if (audio && !hasAudioProducer) {
  // (laat de bestaande audio-produce blok hieronder z’n werk doen)
}


    // If another setLocalStream started while we awaited, abort
    if (gen !== this._localGen) return;

    // Produce audio
    // Produce audio (idempotent)
if (audio) {
  const existing = this.producers.get("audio");

  // Als er al een producer is op exact dezelfde track: niets doen
  if (existing?.track?.id === audio.id) {
    // noop
  } else {
    // Anders: sluit oude audio producer en produce opnieuw
    try {
      existing?.close?.();
    } catch {}
    if (existing) this.producers.delete("audio");

    try {
      const pa = await this.sendT.produce({
        track: audio,
        appData: { kind: "audio" },
      });

      if (gen !== this._localGen) {
        try { pa.close(); } catch {}
        return;
      }

      this.producers.set("audio", pa);

      audio.addEventListener("ended", () => {
        try { pa.close(); } catch {}
        if (this.producers.get("audio") === pa) this.producers.delete("audio");
      });
    } catch (e) {
      console.warn("[PeerManager] produce audio failed:", e);
    }
  }
}


    // Produce video (keep your encodings)
    if (video) {
      try {
        const pv = await this.sendT.produce({
          track: video,
          encodings: [
            { maxBitrate: 150_000 },
            { maxBitrate: 600_000 },
            { maxBitrate: 1_500_000 },
          ],
          codecOptions: { videoGoogleStartBitrate: 1000 },
          appData: { kind: "video" },
        });
        if (gen !== this._localGen) {
          try {
            pv.close();
          } catch {}
          return;
        }
        this.producers.set("video", pv);

        video.addEventListener("ended", () => {
          try {
            pv.close();
          } catch {}
          if (this.producers.get("video") === pv) this.producers.delete("video");
        });
      } catch (e) {
        console.warn("[PeerManager] produce video failed:", e);
      }
    }
  }

  async addLocalTracksToAll() {
    if (this.localStream) await this.setLocalStream(this.localStream);
  }

  // 🔊 Enable/disable local microphone (producer-level, mediasoup-correct)
setMicEnabled(enabled) {
  const p = this.producers.get("audio");
  const stream = this.localStream;

  console.log("[RTC] setMicEnabled =", enabled, "producer =", !!p);

  // 1) Toggle track for local preview
  try {
    const track = stream?.getAudioTracks?.()[0];
    if (track) track.enabled = !!enabled;
  } catch {}

  // 2) Toggle producer for remote peers
  if (p) {
    try {
      if (enabled) {
        p.resume();
      } else {
        p.pause();
      }
    } catch (e) {
      console.warn("[RTC] setMicEnabled failed:", e);
    }
  }
}

setCamEnabled(enabled) {
  const p = this.producers.get("video");
  const stream = this.localStream;

  try {
    const track = stream?.getVideoTracks?.()[0];
    if (track) track.enabled = !!enabled;
  } catch {}

  if (p) {
    try {
      enabled ? p.resume() : p.pause();
    } catch (e) {
      console.warn("[RTC] setCamEnabled failed", e);
    }
  }
}



  destroy() {
    this._closed = true;

    // Stop watchdog timers
    if (this._iceWatchdogTimerSend) {
      clearTimeout(this._iceWatchdogTimerSend);
      this._iceWatchdogTimerSend = null;
    }
    if (this._iceWatchdogTimerRecv) {
      clearTimeout(this._iceWatchdogTimerRecv);
      this._iceWatchdogTimerRecv = null;
    }

    // Unwire socket handlers to prevent duplicate events
    this._unwireSocketHandlers();

    // Close consumers
    try {
      for (const v of this.consumers.values()) {
        const c = v && v.consumer ? v.consumer : v;
        try {
          c?.close?.();
        } catch {}
      }
    } catch {}
    this.consumers.clear();

    // Close producers (do NOT stop capture)
    this._closeLocalProducers();

    // Close transports
    try {
      this.sendT?.close();
    } catch {}
    try {
      this.recvT?.close();
    } catch {}
    this.sendT = null;
    this.recvT = null;

    // Clear remote maps
    this.peerStreams.clear();
    this.prodToPeer.clear();

    // ✅ NEW: clear known producers registry
    this._knownProducers.clear();
  }

  /* -------------------------
   * Internals
   * ------------------------- */

  _closeLocalProducers() {
    for (const p of this.producers.values()) {
      try {
        p.close();
      } catch {}
    }
    this.producers.clear();
  }

  _removePeer(peerId) {
    // Close consumers that belong to this peer
    for (const [prodId, info] of [...this.consumers.entries()]) {
      if (info?.peerId === peerId) {
        try {
          info.consumer?.close?.();
        } catch {}
        this.consumers.delete(prodId);

        // ✅ NEW: remove from known registry
        this._knownProducers.delete(prodId);
      }
    }

    // Remove composite stream
    const ms = this.peerStreams.get(peerId);
    if (ms) {
      try {
        for (const t of ms.getTracks()) {
          try {
            ms.removeTrack(t);
          } catch {}
        }
      } catch {}
      this.peerStreams.delete(peerId);
    }
  }

  async _createTransports() {
    if (!this.device) throw new Error("Device not ready");

    // SEND transport
    const sendP = await new Promise((res) =>
      socket.emit("rtc:createTransport", { direction: "send" }, (r) => res(r))
    );
    if (sendP?.error) throw new Error(sendP.error);

    this.sendT = this.device.createSendTransport(sendP);

    this.sendT.on("connect", ({ dtlsParameters }, cb, errCb) => {
      socket.emit(
        "rtc:connectTransport",
        { transportId: this.sendT.id, dtlsParameters },
        (r) => (r?.ok ? cb() : errCb(r?.error || "connect send failed"))
      );
    });

    this.sendT.on("produce", ({ kind, rtpParameters, appData }, cb, errCb) => {
      socket.emit(
        "rtc:produce",
        { transportId: this.sendT.id, kind, rtpParameters, appData },
        ({ id, error }) => (error ? errCb(error) : cb({ id }))
      );
    });

    this._watchTransport(this.sendT, "send");

    // RECV transport
    const recvP = await new Promise((res) =>
      socket.emit("rtc:createTransport", { direction: "recv" }, (r) => res(r))
    );
    if (recvP?.error) throw new Error(recvP.error);

    this.recvT = this.device.createRecvTransport(recvP);

    this.recvT.on("connect", ({ dtlsParameters }, cb, errCb) => {
      socket.emit(
        "rtc:connectTransport",
        { transportId: this.recvT.id, dtlsParameters },
        (r) => (r?.ok ? cb() : errCb(r?.error || "connect recv failed"))
      );
    });

    this._watchTransport(this.recvT, "recv");
  }

  _watchTransport(transport, label) {
    if (!transport) return;

    transport.on("connectionstatechange", (state) => {
      console.warn(`[RTC] ${label} transport state:`, state);

      const key =
        label === "send" ? "_iceWatchdogTimerSend" : "_iceWatchdogTimerRecv";

      // connected -> clear watchdog
      if (state === "connected") {
        if (this[key]) {
          clearTimeout(this[key]);
          this[key] = null;
        }
      }

      // disconnected -> start watchdog
      if (state === "disconnected") {
        if (this[key]) clearTimeout(this[key]);
        this[key] = setTimeout(() => {
          this._maybeRecover({
            reason: `transport-${label}-disconnected-timeout`,
            kind: label,
          });
        }, this._iceWatchdogMs);
      }

      // failed -> immediate recover
      if (state === "failed") {
        if (this[key]) {
          clearTimeout(this[key]);
          this[key] = null;
        }
        this._maybeRecover({ reason: `transport-${label}-failed`, kind: label });
      }
    });

    transport.on("close", () => {
      if (this._closed) return;
      this._maybeRecover({ reason: `transport-${label}-close`, kind: label });
    });
  }

  _maybeRecover({ reason = "unknown", kind = "unknown" } = {}) {
    if (this._closed) return;
    if (this._recovering) return;

    const now = Date.now();
    const COOLDOWN_MS = 4500;

    if (kind === "send") {
      if (now - this._lastRecoverAtSend < COOLDOWN_MS) return;
      this._lastRecoverAtSend = now;
    } else if (kind === "recv") {
      if (now - this._lastRecoverAtRecv < COOLDOWN_MS) return;
      this._lastRecoverAtRecv = now;
    }

    console.warn("[RTC] starting recovery:", { reason, kind });

    this._recovering = true;

    this._recover({ reason, kind })
      .catch((e) => console.error("[RTC] recovery failed:", e))
      .finally(() => {
        this._recovering = false;
      });
  }

  async _recover({ reason = "unknown", kind = "unknown" } = {}) {
    if (this._closed) return;

    console.warn("[RTC] recovering transports…", { reason, kind });

    try {
      // 1) Close transports
      try {
        this.sendT?.close();
      } catch {}
      try {
        this.recvT?.close();
      } catch {}
      this.sendT = null;
      this.recvT = null;

      // 2) Close all consumers
      try {
        for (const v of this.consumers.values()) {
          const c = v && v.consumer ? v.consumer : v;
          try {
            c?.close?.();
          } catch {}
        }
      } catch {}
      this.consumers.clear();

      // ✅ NEW: clear known registry (we will re-sync)
      this._knownProducers.clear();

      // 3) Clear remote maps + notify UI in one shot
      try {
        this.onPeerRemove?.("*");
      } catch {}
      this.peerStreams.clear();
      this.prodToPeer.clear();

      // 4) Recreate transports
      await this._createTransports();

      // 5) Re-produce local tracks WITHOUT stopping capture
      this._closeLocalProducers();

      const ls = this.localStream || this.getLocalStream?.() || null;
      if (ls) {
        this.localStream = ls;
        await this.setLocalStream(ls);
      }

      // 6) Re-sync producer list by re-joining (idempotent on server)
      const res = await new Promise((res) =>
        socket.emit("rtc:join", {}, (r) => res(r))
      );

      const producers = Array.isArray(res?.producers) ? res.producers : [];
      for (const { producerId, peerId } of producers) {
        if (!producerId) continue;
        if (peerId) this.prodToPeer.set(producerId, peerId);
        try {
          await this._consume(producerId);
        } catch {}
      }

      // ✅ NEW: safety net to ensure no producers were missed
      await this._syncProducersOnce(4);

      // ✅ success: reset fail streak
      this._recoverFailStreak = 0;
      console.warn("[RTC] recovery completed");
    } catch (e) {
      this._recoverFailStreak = (this._recoverFailStreak || 0) + 1;
      console.warn("[RTC] recovery attempt failed", {
        reason,
        kind,
        failStreak: this._recoverFailStreak,
        e,
      });

      // Escalate after 2 consecutive failures (with cooldown)
      const now = Date.now();
      const FULL_RESET_COOLDOWN_MS = 15000;
      const canReset =
        now - (this._lastFullResetAt || 0) > FULL_RESET_COOLDOWN_MS;

      if (this._recoverFailStreak >= 2 && canReset) {
        this._lastFullResetAt = now;
        console.warn("[RTC] escalating to FULL RESET");
        await this._fullReset();
      }
    }
  }

  async _fullReset() {
    if (this._closed) return;

    // hard close everything
    try {
      this.sendT?.close();
    } catch {}
    try {
      this.recvT?.close();
    } catch {}
    this.sendT = null;
    this.recvT = null;

    // close consumers
    try {
      for (const v of this.consumers.values()) {
        const c = v && v.consumer ? v.consumer : v;
        try {
          c?.close?.();
        } catch {}
      }
    } catch {}
    this.consumers.clear();

    // close producers (do NOT stop capture)
    this._closeLocalProducers();

    // UI clear
    try {
      this.onPeerRemove?.("*");
    } catch {}
    this.peerStreams.clear();
    this.prodToPeer.clear();

    // ✅ NEW: clear known registry
    this._knownProducers.clear();

    // recreate device (rare stuck state protection)
    this.device = null;

    // join again (re-load device + transports)
    await this.joinRoom();

    // restore local sending if available
    const ls = this.localStream || this.getLocalStream?.() || null;
    if (ls) {
      this.localStream = ls;
      await this.setLocalStream(ls);
    }

    // ✅ NEW: final safety sync
    await this._syncProducersOnce(4);

    this._recoverFailStreak = 0;
    console.warn("[RTC] FULL RESET OK");
  }

  async _consume(producerId) {
    if (this._closed) return;
    if (!this.recvT || !this.device) return;

    // ✅ NEW: if already known, skip (prevents duplicate consume attempts)
    if (this._knownProducers.has(producerId)) return;

    // If already consuming, skip
    if (this.consumers.has(producerId)) return;

    const params = await new Promise((res) =>
      socket.emit(
        "rtc:consume",
        {
          transportId: this.recvT.id,
          producerId,
          rtpCapabilities: this.device.rtpCapabilities,
        },
        (r) => res(r)
      )
    );

    if (!params || params.error) return;

    const consumer = await this.recvT.consume({
      id: params.id,
      producerId: params.producerId,
      kind: params.kind,
      rtpParameters: params.rtpParameters,
    });

    const peerId = params.peerId || this.prodToPeer.get(producerId) || "peer";

    this.consumers.set(producerId, {
      consumer,
      peerId,
      kind: params.kind,
    });

 

    const removeTrackFromPeer = () => {
      const cur = this.peerStreams.get(peerId);
      if (!cur) return;

      const left = cur.getTracks().filter((t) => t.id !== consumer.track.id);

      if (left.length) {
        const next = new MediaStream(left);
        this.peerStreams.set(peerId, next);
        this.onPeerStream?.(peerId, next);
      } else {
        this.peerStreams.delete(peerId);
        this.onPeerRemove?.(peerId);
      }
    };

    consumer.on("producerclose", () => {
      console.warn("[RTC] producerclose:", producerId, "peerId:", peerId);
      try {
        consumer.close();
      } catch {}
      this.consumers.delete(producerId);

      // ✅ NEW: unmark known
      this._knownProducers.delete(producerId);

      removeTrackFromPeer();
    });

    consumer.on("transportclose", () => {
      try {
        consumer.close();
      } catch {}
      this.consumers.delete(producerId);

      // ✅ NEW: unmark known
      this._knownProducers.delete(producerId);

      removeTrackFromPeer();
    });

    try {
      consumer.track?.addEventListener?.("ended", removeTrackFromPeer);
    } catch {}

    // Merge into a NEW MediaStream reference (prevents video element stalling)
    const existing = this.peerStreams.get(peerId) || new MediaStream();
    const tracks = [
      ...existing.getTracks().filter((t) => t.kind !== consumer.kind),
      consumer.track,
    ];
    const merged = new MediaStream(tracks);
    this.peerStreams.set(peerId, merged);

    // Resume consumer before notifying UI (ACK + retry; otherwise inbound-rtp(audio) may never start)
const resumeOnce = () =>
  new Promise((resolve) => {
    socket.emit("rtc:resume", { consumerId: consumer.id }, (r) => resolve(r));
  });

let resumed = false;
for (let i = 0; i < 3; i++) {
  const r = await resumeOnce();
console.log("[RTC] resume ack", r, {
  attempt: i + 1,
  producerId,
  consumerId: consumer.id,
  peerId,
  kind: consumer.kind,
});
if (r?.ok) {
  resumed = true;
  break;
}

  await new Promise((t) => setTimeout(t, 150));
}

if (!resumed) {
  console.warn("[RTC] resume failed; closing consumer", {
    producerId,
    consumerId: consumer.id,
    peerId,
  });

  // Important: allow re-consume later
  this.consumers.delete(producerId);
  this._knownProducers.delete(producerId);

  try { consumer.close(); } catch {}
  removeTrackFromPeer(); // clean any partial merge

  return; // do NOT notify UI with a “silent” stream
}

// ✅ Only now mark producer as known (resume succeeded)
this._knownProducers.add(producerId);

try {
  const aCount = merged.getAudioTracks().length;
  const vCount = merged.getVideoTracks().length;
  console.log("[RTC] merged stream for", peerId, { aCount, vCount, kind: consumer.kind });
} catch {}

    this.onPeerStream?.(peerId, merged);
  }
}

export default PeerManager;
export { PeerManager };
